import Storage from "./Storage";

export const UTM_PARAMS_STORAGE_KEY = "utmParams";

const TRACKED_PARAMS = [
    "utm_campaign",
    "utm_content",
    "utm_id",
    "utm_medium",
    "utm_source",
    "utm_term",
    "thm",
    ...Array.from({ length: 10 }, (_, index) => `aff_sub${index}`),
];

const THIRTY_DAYS_IN_SECONDS = 60 * 60 * 24 * 30;

/**
 * The SPA equivalent of CookieSettingsAndAnalyticsMiddleware::saveUtmParamsToSession.
 * On the SPA host first-party navigations never reach Laravel, so that middleware
 * never runs. Landing-page attribution params are persisted here and replayed to the
 * backend inside the register POST body (and on the Google signup redirect), where
 * the middleware picks them up into the session.
 */
export function captureUtmParams() {
    const queryParams = new URLSearchParams(globalThis.location.search);
    const storedParams = loadUtmParams();
    let hasNewValues = false;

    for (const param of TRACKED_PARAMS) {
        const value = queryParams.get(param);
        if (value !== null) {
            storedParams[param] = value;
            hasNewValues = true;
        }
    }

    // subthm is a subcategory of thm, so it is only ever stored or replaced together with its thm.
    if (queryParams.has("thm")) {
        const subthm = queryParams.get("subthm");
        if (subthm !== null) {
            storedParams.subthm = subthm;
        } else {
            delete storedParams.subthm;
        }
    }

    if (hasNewValues) {
        Storage.save(UTM_PARAMS_STORAGE_KEY, storedParams, {
            secondsTillExpiry: THIRTY_DAYS_IN_SECONDS,
        });
    }
}

export function loadUtmParams(): Record<string, string> {
    return Storage.load<Record<string, string>>(UTM_PARAMS_STORAGE_KEY, {});
}

/**
 * Puts the persisted landing params on a URL. Needed for top-level navigations
 * to Laravel — the Google signup handshake — where the query string is the only
 * way the session learns about them.
 */
export function withUtmParams(url: string): string {
    const target = new URL(url, globalThis.location.origin);

    for (const [key, value] of Object.entries(loadUtmParams())) {
        target.searchParams.set(key, value);
    }

    return target.toString();
}

/**
 * The SPA equivalent of Laravel's session('thm') campaign theme check:
 * the current URL wins, falling back to the persisted landing params.
 * thm and subthm always come from the same source so they stay a pair.
 */
function loadThemeParam(param: "thm" | "subthm"): string | null {
    const queryParams = new URLSearchParams(globalThis.location.search);
    const value = queryParams.has("thm")
        ? queryParams.get(param)
        : loadUtmParams()[param];
    return value ? value.toLowerCase() : null;
}

export function loadThm(): string | null {
    return loadThemeParam("thm");
}

export function loadSubthm(): string | null {
    return loadThemeParam("subthm");
}
